<template>
  <UList>
    <UBreadcrumbs :items="breadcrumbItems" />

    <UProgressBar
      v-if="isDataReady"
      green
      is-loader
    />

    <div
      v-else
      class="w-full"
    >
      <UContent :has-title-delimiter="true">
        <template #header>
          Withdraw
        </template>
        <template #content>
          <div>Choose available currency to add funds to your account.</div>
          <URow>
            <div class="left">
              <div class="amount">
                <div class="one">
                  <UInput
                    placeholder="Select currency"
                    type="select"
                    :options="currencyOpt"
                    @update:modelValue="(v) => currencyId = v"
                  />
                </div>
                <div class="two">
                  <UInput
                    v-model="amount"
                    type="number"
                    placeholder="Enter amount"
                  />
                </div>
              </div>
              <div
                v-if="!hideAuthCode"
                class="auth"
              >
                <div>Enter the code from the Google Authenticator app</div>
                <AuInput
                  v-model="authCode"
                  placeholder="Auth code"
                  @input="checkAuth"
                />
              </div>
            </div>
            <UList class="right">
              <BankCard
                header="Withdraw form"
                descriptions="Add a banks account to withdraw worldwide"
                @cardNumberChange="onCardNumberChange"
              />
              <div>
                By confirming your withdrawal request, you agree to the
                <a
                  class="link"
                  :href="`${hostLink}payment-terms`"
                  target="_blank"
                >Payment terms</a>
              </div>
              <UButton
                :disabled="!isBtnActive"
                @click="onWithdraw"
              >
                Withdraw
              </UButton>
            </UList>
          </URow>
        </template>
      </UContent>
    </div>
  </UList>
</template>

<script setup>
import _ from "lodash";
import AuInput from "@/components/extended/AuInput.vue";
import auth from "@/auth";
import BankCard from "@/components/buyForm/BankCard.vue";
import URow from "@/components/uikit//uLayouts/URow.vue";
import { checkAccess, scope } from "@/help/accessData";
import { computed, ref } from "vue";
import { useI18n } from "@/localization/localizator";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useUser } from "@/composables/useAuth";

const t = useI18n();
const store = useStore();
const toast = useToast();
const router = useRouter();
const isDataReady = ref(false);
const loading = ref(false);
const amount = ref(null);
const currencyId = ref(null);
const cardNumber = ref(null);
const user = ref(useUser());
const authCode = ref("");

// Computeds
const currency = computed(() => _.find(currencyOpt.value, { value: currencyId.value })?.display);

const breadcrumbItems = computed(() => [
  { key: 0, label: t("router.pages.dashboard"), route: "/" },
  { key: 1, label: t("router.pages.briefcase"), route: "/my-assets" },
  { key: 2, label: "Deposit", route: "/my-assets/card-buy-form" }
]);
const hideAuthCode = computed(() => {
  return checkAccess(scope.testRole);
});
const hostLink = computed(() => process.env.VUE_APP_HOST);
const auOwners = computed(() => store.getters.auOwners);


const currencyOpt = computed(() => {
  if (!auOwners.value?.length) {
    return [
      {
        display: "AED",
        value: 1
      },
      {
        display: "USD",
        value: 2
      }
    ];
  }

  return auOwners.value.map((item) => ({
    display: item.auToken.nameEng,
    value: item.auToken.id
  }));
});
const isBtnActive = computed(() => amount.value && currency.value && ((authCode.value.toString() ?? "").length === 6 || hideAuthCode.value));

// Methods
const checkAuth = (e) => {
  authCode.value = e.target.value.replace(/\D/g, "");
};
const onWithdraw = async () => {
  loading.value = true;
  if (!auth.user().is2faEnabled) {
    toast.error("Unable to withdraw funds. Enable two-factor authentication in your profile to fix this.");
    return;
  }

  let params = {
    address: "",
    createdAt: new Date,
    description: "withdraw",
    isTask: true,
    token: `/api/au_tokens/${currency.value}`,
    type: "token_withdrawal",
    user: `api/users/${user.value.id}`,
    value: String(amount.value)
  };
  if (!hideAuthCode.value) {
    params.authCode = authCode.value;
  }

  try {
    const resp = await store.dispatch("sendTokenEvents", params);
    if (resp.status === 267) {
      toast.error((resp?.data?.error ?? resp?.data?.message) ?? "Unknown error");
    }
    else {
      toast.success("Successful withdrawal");
      setTimeout(() => {
        router.push("/transactions?type=all");
      }, 5000000);
    }
  }
  catch (error) {
    toast.error("An error has occurred", error?.messages);
    console.error("Error:", error);
  }
  finally {
    loading.value = false;
  }
};

const onCardNumberChange = (value) => {
  cardNumber.value = value;
};
</script>

<style scoped lang="scss">
.left,
.right {
  flex: 1 1 0;
  box-sizing: border-box;
}

.left {
  display: flex;
  flex-direction: column;
  gap: 30px;

  .amount {
    display: flex;
    gap: 8px;

    .one {
      flex: 0 0 20%;
    }

    .two {
      flex: 1;
    }
  }

  .auth {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

.link {
  text-decoration: underline !important;
  color: var(--text-color);
}
</style>
